import { createAccessControl } from "better-auth/plugins/access";
import {
  defaultStatements as defaultAdminStatements,
  adminAc as defaultAdminAc,
  userAc as defaultUserAc,
} from "better-auth/plugins/admin/access";
import {
  defaultStatements as defaultOrganizationStatements,
  adminAc as orgAdminAc,
  memberAc as orgMemberAc,
  ownerAc as orgOwnerAc,
} from "better-auth/plugins/organization/access";

export type UserRole = "user" | "admin";
export type MemberRole = "admin" | "owner" | "member";

// Global roles & permissions

export const adminStatement = {
  ...defaultAdminStatements,
} as const;

export const adminAc = createAccessControl(adminStatement);

export const adminRoles = {
  user: adminAc.newRole({
    ...defaultUserAc.statements,
  }),
  admin: adminAc.newRole({
    ...defaultAdminAc.statements,
  }),
};

// Define new permissions for custom roles here
export const orgStatement = {
  ...defaultOrganizationStatements,
  generalAnalysis: ["read", "create", "update", "delete"],
  statementMatching: ["read", "create", "update", "delete"],
  eomReview: ["read", "create", "update", "delete"],
  vendorInvoice: ["read", "create", "update", "delete", "review", "approve", "authorize"],
  purchaseJournal: ["read", "create", "update", "delete"],
} as const;

export const orgAc = createAccessControl(orgStatement);

export const orgRoles = {
  admin: orgAc.newRole({
    ...orgAdminAc.statements,
    organization: ["update", "delete"],
    generalAnalysis: ["read", "create", "update", "delete"],
    statementMatching: ["read", "create", "update", "delete"],
    eomReview: ["read", "create", "update", "delete"],
    vendorInvoice: ["read", "create", "update", "delete", "review", "approve", "authorize"],
    purchaseJournal: ["read", "create", "update", "delete"],
  }),
  owner: orgAc.newRole({
    ...orgOwnerAc.statements,
    generalAnalysis: ["read", "create", "update", "delete"],
    statementMatching: ["read", "create", "update", "delete"],
    eomReview: ["read", "create", "update", "delete"],
    vendorInvoice: ["read", "create", "update", "delete", "review", "approve", "authorize"],
    purchaseJournal: ["read", "create", "update", "delete"],
  }),
  // Purchase journals are intentionally NOT granted to `member` by default:
  // only owner/admin get access out of the box; every other role must be
  // explicitly granted the `purchaseJournal` permission via the roles editor.
  member: orgAc.newRole({
    ...orgMemberAc.statements,
    generalAnalysis: ["read", "create", "update", "delete"],
    statementMatching: ["read", "create"],
    eomReview: ["read", "create"],
    vendorInvoice: ["read", "create"],
  }),
};

export const PERMISSION_LABELS: Record<string, { label: string; description: string }> = {
  organization: { label: "Organization", description: "Manage organization settings" },
  member: { label: "Members", description: "Manage organization members" },
  invitation: { label: "Invitations", description: "Manage member invitations" },
  team: { label: "Teams", description: "Manage teams within the organization" },
  ac: { label: "Access Control", description: "Manage roles and permissions" },
  generalAnalysis: { label: "General Analysis", description: "Access general analysis features" },
  statementMatching: { label: "Statement Matching", description: "Access statement matching features" },
  eomReview: { label: "EOM Review", description: "Access end-of-month review features" },
  vendorInvoice: { label: "Vendor Invoice", description: "Access vendor invoice features" },
  purchaseJournal: { label: "Purchase Journal", description: "Access purchase journal features" },
};

export const PERMISSION_ACTION_LABELS: Record<string, string> = {
  create: "Create",
  read: "Read",
  update: "Update",
  delete: "Delete",
  cancel: "Cancel",
  review: "Review",
  approve: "Approve",
  authorize: "Authorize",
};
